import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { computeTip, validate, checkTipPct, defaultPreTax, defaultCurrency, fractionDigits, CURRENCIES, TIP_HINTS, SERVICE_PRESETS, COUNTRY_TIPS, tipFromQuery, tipToQuery, type TipShare } from "./tip";

// Comparison strip, uneven split, reverse mode, working and export load on demand.
const TipExtras = lazy(() => import("./TipExtras"));
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Calculates a tip amount and the total bill based on your chosen tip percentage, and can split the total evenly across a group.`,
  how: `Enter the bill amount, choose or enter a tip percentage, and optionally the number of people splitting the bill. The tip amount, total, and per-person share are calculated automatically.`,
  tip: `If the group had noticeably different orders, splitting evenly isn't always fair — this tool handles the even split well, but for itemized splitting you'd want to divide the bill by item first.`,
};

export default function TipCalculator() {
  const [defaults] = useState<TipShare>(() => ({ bill: "50", tax: "", tipPct: "18", people: "1", currency: defaultCurrency(typeof navigator !== "undefined" ? navigator.language : undefined), preTax: "", roundUp: false }));
  const [init] = useState(() => (typeof window === "undefined" ? defaults : tipFromQuery(window.location.search, defaults)));
  const [bill, setBill] = useState(init.bill);
  const [tax, setTax] = useState(init.tax);
  const [tipPct, setTipPctRaw] = useState(init.tipPct);
  const [people, setPeople] = useState(init.people);
  const [currency, setCurrency] = useState(init.currency);
  const [preTaxChoice, setPreTaxChoice] = useState<boolean | null>(init.preTax === "" ? null : init.preTax === "1");
  const preTax = preTaxChoice ?? defaultPreTax(currency);
  const [roundUp, setRoundUp] = useState(init.roundUp);
  // A service preset can be a flat amount (hotel housekeeping); typing a percentage goes back to percent mode.
  const [flat, setFlat] = useState<number | null>(null);
  const [presetId, setPresetId] = useState("");
  const setTipPct = (v: string) => { setFlat(null); setPresetId(""); setTipPctRaw(v); };
  const applyPreset = (id: string) => {
    setPresetId(id);
    const pr = SERVICE_PRESETS.find((x) => x.id === id);
    if (!pr) { setFlat(null); return; }
    if (pr.flat !== undefined) setFlat(pr.flat); else { setFlat(null); setTipPctRaw(String(pr.pct)); }
  };
  // Shareable link: the typed numbers only (replaceState, nothing stored).
  useEffect(() => {
    try {
      const q = tipToQuery({ bill, tax, tipPct, people, currency, preTax: preTaxChoice === null ? "" : preTaxChoice ? "1" : "0", roundUp }, defaults);
      window.history.replaceState(null, "", `${window.location.pathname}${q ? `?${q}` : ""}${window.location.hash}`);
    } catch { /* ignore */ }
  }, [bill, tax, tipPct, people, currency, preTaxChoice, roundUp, defaults]);

  const digits = fractionDigits(currency);
  const money = (v: number) => { try { return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(v); } catch { return v.toFixed(digits); } };

  const check = useMemo(() => validate(bill, tax, people), [bill, tax, people]);
  const pctCheck = useMemo(() => checkTipPct(tipPct), [tipPct]);
  const pctOk = pctCheck.ok;
  const pct = pctCheck.ok ? pctCheck.pct : 0;
  const tipInput = useMemo(() => (check.ok && pctOk ? { bill: check.bill, tax: check.tax, tipPct: pct, people: check.people, tipOnPreTax: preTax, roundUpPerPerson: roundUp, digits, ...(flat !== null ? { flatTip: flat } : {}) } : null), [check, pctOk, pct, preTax, roundUp, digits, flat]);
  const r = useMemo(() => (tipInput ? computeTip(tipInput) : null), [tipInput]);

  const { blocked, gate } = useLogToolUsage(!!r, 3000);
  const hi = r ? Math.max(...r.shares) : 0, lo = r ? Math.min(...r.shares) : 0;

  return (
    <ToolContainer>
      <div className="max-w-md mx-auto space-y-6">
        <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
          <div><Label htmlFor="tip-bill">Bill Amount</Label><Input type="number" min={0} step="any" value={bill} onChange={e => setBill(e.target.value)} id="tip-bill" aria-label="Bill amount" /></div>
          <div>
            <Label>Currency</Label>
            <select aria-label="Currency" value={currency} onChange={e => setCurrency(e.target.value)} className="border rounded-md px-2 py-2 bg-background text-sm block">
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div><Label>Tax (optional)</Label><Input type="number" min={0} step="any" value={tax} onChange={e => setTax(e.target.value)} aria-label="Tax amount" placeholder="0" /></div>
        <div className="flex items-center gap-2"><Switch id="tip-pretax" checked={preTax} onCheckedChange={setPreTaxChoice} aria-label="Tip on the amount before tax" /><Label htmlFor="tip-pretax" className="text-sm">Tip on the amount before tax</Label></div>
        <div>
          <Label>Tip: {pctOk ? `${pct}%` : "—"}</Label>
          <div className="flex flex-wrap gap-2 my-2">
            {[10, 15, 18, 20, 25].map(p => <Button key={p} size="sm" variant={pct === p ? "default" : "outline"} onClick={() => setTipPct(String(p))}>{p}%</Button>)}
            <Input className="w-24 h-9" type="number" min={0} step="any" value={tipPct} aria-invalid={!pctOk} onChange={e => setTipPct(e.target.value)} aria-label="Custom tip percent" />
          </div>
          <Slider value={[pctOk ? Math.min(50, pct) : 0]} onValueChange={v => setTipPct(String(v[0]))} min={0} max={50} step={1} />
          <div className="mt-3">
            <Label htmlFor="tip-service">Service</Label>
            <select id="tip-service" className="border rounded-md px-2 py-2 bg-background text-sm block w-full" value={presetId} onChange={(e) => applyPreset(e.target.value)}>
              <option value="">Choose a service (optional)</option>
              {SERVICE_PRESETS.map((x) => <option key={x.id} value={x.id}>{x.label} ({x.range})</option>)}
            </select>
            {presetId && <p className="text-xs text-muted-foreground mt-1" data-testid="tip-preset-note">{SERVICE_PRESETS.find((x) => x.id === presetId)?.note} Based on common US etiquette; customs vary.</p>}
            {flat !== null && <p className="text-xs mt-1" data-testid="tip-flat-note">Flat tip of {money(flat)} applied. Type a percentage to go back to a percent tip.</p>}
          </div>
          {(COUNTRY_TIPS[currency] || TIP_HINTS[currency]) && <p className="text-xs text-muted-foreground mt-2" data-testid="tip-country-note">{COUNTRY_TIPS[currency] ? <strong>{COUNTRY_TIPS[currency].customary[0].toUpperCase() + COUNTRY_TIPS[currency].customary.slice(1)}. </strong> : null}{TIP_HINTS[currency]}</p>}
        </div>
        <div><Label>Split Between</Label><Input type="number" min="1" step="1" value={people} onChange={e => setPeople(e.target.value)} aria-label="Number of people" /></div>
        <div className="flex items-center gap-2"><Switch id="tip-roundup" checked={roundUp} onCheckedChange={setRoundUp} aria-label="Round each share up" /><Label htmlFor="tip-roundup" className="text-sm">Round each share up to a whole {currency} (tip rises a little)</Label></div>
        {!check.ok && <p className="text-sm text-destructive" role="alert" data-testid="tip-error">{check.message}</p>}
        {check.ok && !pctCheck.ok && <p className="text-sm text-destructive" role="alert" data-testid="tip-pct-error">{pctCheck.message}</p>}
        {pctCheck.ok && pctCheck.warning && <NoticeBox className="text-sm" role="status" testId="tip-pct-warning">{pctCheck.warning}</NoticeBox>}
        <div className="grid grid-cols-3 gap-4 pt-4 border-t">
          <div className="text-center p-3 rounded-lg border bg-card"><p className="text-xs text-muted-foreground">Tip</p><p className="text-lg font-bold" data-testid="tip-amount">{blocked || !r ? "—" : money(r.tip)}</p></div>
          <div className="text-center p-3 rounded-lg border bg-card"><p className="text-xs text-muted-foreground">Total</p><p className="text-lg font-bold" data-testid="tip-total">{blocked || !r ? "—" : money(r.total)}</p></div>
          <div className="text-center p-3 rounded-lg border bg-primary/5"><p className="text-xs text-muted-foreground">Per Person</p><p className="text-lg font-bold text-primary" data-testid="tip-per">{blocked || !r ? "—" : r.allEqual ? money(hi) : `${money(lo)}–${money(hi)}`}</p></div>
        </div>
        {r && !blocked && !r.allEqual && (
          <p className="text-sm text-muted-foreground text-center" data-testid="tip-split-note">
            {r.shares.filter(x => x === hi).length} pay {money(hi)} and {r.shares.filter(x => x === lo).length} pay {money(lo)}, so the shares add up exactly to {money(r.total)}.
          </p>
        )}
        {r && tipInput && check.ok && (
          <Suspense fallback={null}>
            <TipExtras input={tipInput} result={r} money={money} currency={currency} gate={gate} />
          </Suspense>
        )}
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
